import { useState } from 'react';
import {
  Card, Group, Text, Badge, Stack, Button, Modal, TextInput, Switch, ActionIcon,
  SimpleGrid, Image, FileButton, Loader, Center, Box, Tooltip, NumberInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconSticker2, IconTrash, IconPencil, IconUpload } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api, { fileUrl } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { EmptyState } from '../components/EmptyState';
import { confirmDelete } from '../lib/confirm';
import { PREMIUM_UI_ENABLED } from '../lib/features';

interface Sticker { id: string; imageKey: string; order: number }
interface StickerPack {
  id: string; title: string; order: number; isActive: boolean; stickers: Sticker[];
}

/**
 * Stiker paketlari (ilova avatarlari) — rasm-ico stikerlar CRUD.
 * Rasmlar barcha platformada bir xil ko'rinadi (barcha qurilmalarda bir xil).
 *
 * PREMIUM MUZLATISH: bu bo'lim premiumga BOG'LIQ EMAS va yashirilmaydi —
 * `PREMIUM_UI_ENABLED = false` bo'lganda ilovada avatar tanlash HAMMA ota-onaga
 * ochiq (`app/src/screens/ProfileScreen.tsx` → `canEditAvatar`), ya'ni paketlar
 * kerak bo'lib turibdi. Shu sababli sarlavhadan "Premium" so'zi tushib qoladi.
 * Matn O'CHIRILMAGAN, bayroq ostida: muzlatish qaytarilsa (`PREMIUM_UI_ENABLED = true`)
 * avatar tanlash yana faqat premium ota-onalarga ochiladi va sarlavha ham qaytadi.
 */
export default function Stickers() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editPack, setEditPack] = useState<StickerPack | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  // Drag&drop tartiblash holati
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  // Bosilganda katta ko'rinish
  const [preview, setPreview] = useState<Sticker | null>(null);

  const { data: packs, isLoading } = useQuery({
    queryKey: ['sticker-packs'],
    queryFn: async () => (await api.get<StickerPack[]>('/stickers/packs')).data,
  });

  const selected = (packs ?? []).find((p) => p.id === selectedId) ?? (packs ?? [])[0] ?? null;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['sticker-packs'] });

  const form = useForm({
    initialValues: { title: '', order: 0 },
    validate: { title: (v) => (!v.trim() ? t('pages.stickers.titleRequired') : null) },
  });

  const saveMut = useMutation({
    mutationFn: (v: typeof form.values) =>
      editPack
        ? api.patch(`/stickers/packs/${editPack.id}`, v)
        : api.post('/stickers/packs', v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('common.save') });
      invalidate(); form.reset(); setEditPack(null); close();
    },
  });

  const toggleMut = useMutation({
    mutationFn: (p: StickerPack) => api.patch(`/stickers/packs/${p.id}`, { isActive: !p.isActive }),
    onSuccess: invalidate,
  });

  const deletePackMut = useMutation({
    mutationFn: (id: string) => api.delete(`/stickers/packs/${id}`),
    onSuccess: () => { invalidate(); setSelectedId(null); },
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`/stickers/packs/${selected!.id}/stickers`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => { notifications.show({ color: 'green', message: t('pages.stickers.uploaded') }); invalidate(); },
  });

  const deleteStickerMut = useMutation({
    mutationFn: (id: string) => api.delete(`/stickers/${id}`),
    onSuccess: invalidate,
  });

  const reorderMut = useMutation({
    mutationFn: (v: { packId: string; ids: string[] }) =>
      api.patch(`/stickers/packs/${v.packId}/reorder`, { ids: v.ids }),
    onSuccess: invalidate,
  });

  /** Drag&drop: fromId stikerini toId o'rniga ko'chirish — optimistik + serverga saqlash */
  const moveSticker = (fromId: string, toId: string) => {
    if (!selected || fromId === toId) return;
    const ids = selected.stickers.map((s) => s.id);
    const from = ids.indexOf(fromId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ...ids.splice(from, 1));
    // Optimistik: keshda yangi tartib darhol ko'rinadi
    qc.setQueryData<StickerPack[]>(['sticker-packs'], (old) =>
      (old ?? []).map((p) =>
        p.id === selected.id
          ? { ...p, stickers: ids.map((id) => p.stickers.find((s) => s.id === id)!).filter(Boolean) }
          : p
      )
    );
    reorderMut.mutate({ packId: selected.id, ids });
  };

  const openCreate = () => { setEditPack(null); form.setValues({ title: '', order: 0 }); open(); };
  const openEdit = (p: StickerPack) => { setEditPack(p); form.setValues({ title: p.title, order: p.order }); open(); };

  return (
    <>
      <PageHeader
        title={PREMIUM_UI_ENABLED ? t('pages.stickers.titlePremium') : t('pages.stickers.title')}
        subtitle={PREMIUM_UI_ENABLED ? t('pages.stickers.subtitlePremium') : t('pages.stickers.subtitle')}
        action={<Button leftSection={<IconPlus size={18} />} onClick={openCreate}>{t('pages.stickers.addPack')}</Button>}
      />

      {isLoading ? (
        <Center h={160}><Loader /></Center>
      ) : !packs?.length ? (
        <EmptyState icon={IconSticker2} title={t('pages.stickers.empty')} description={t('pages.stickers.emptyDesc')} />
      ) : (
        <Group align="flex-start" gap="md" wrap="nowrap">
          {/* Paketlar ro'yxati */}
          <Stack gap="xs" w={300} style={{ flexShrink: 0 }}>
            {packs.map((p) => (
              <Card
                key={p.id}
                padding="sm"
                withBorder={selected?.id === p.id}
                style={{ cursor: 'pointer', borderColor: selected?.id === p.id ? 'var(--mantine-color-brand-5)' : undefined }}
                onClick={() => setSelectedId(p.id)}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Box style={{ minWidth: 0 }}>
                    <Text fw={600} size="sm" lineClamp={1}>{p.title}</Text>
                    <Text size="xs" c="dimmed">{p.stickers.length} {t('pages.stickers.count')}</Text>
                  </Box>
                  <Group gap={6} wrap="nowrap">
                    <Tooltip label={p.isActive ? t('pages.stickers.active') : t('pages.stickers.inactive')}>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Switch size="xs" checked={p.isActive} onChange={() => toggleMut.mutate(p)} disabled={toggleMut.isPending} />
                      </div>
                    </Tooltip>
                    <ActionIcon variant="subtle" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(p); }}>
                      <IconPencil size={15} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle" color="red" size="sm"
                      onClick={(e) => { e.stopPropagation(); confirmDelete(p.title, () => deletePackMut.mutate(p.id)); }}
                     loading={deletePackMut.isPending}>
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            ))}
          </Stack>

          {/* Tanlangan paket stikerlari — sarlavha boshqa kartalardagi kabi header'da */}
          <SectionCard
            title={selected?.title ?? t('pages.stickers.title')}
            icon={IconSticker2}
            style={{ flex: 1 }}
            right={selected ? (
              <Group gap="xs" wrap="nowrap">
                {!selected.isActive && <Badge color="gray" variant="light">{t('pages.stickers.inactive')}</Badge>}
                <FileButton onChange={(f) => f && uploadMut.mutate(f)} accept="image/png,image/webp,image/jpeg,image/x-icon,image/gif">
                  {(props) => (
                    <Button {...props} size="xs" leftSection={<IconUpload size={15} />} loading={uploadMut.isPending}>
                      {t('pages.stickers.upload')}
                    </Button>
                  )}
                </FileButton>
              </Group>
            ) : undefined}
          >
            {!selected ? (
              <Center h={120}><Text c="dimmed">{t('pages.stickers.selectPack')}</Text></Center>
            ) : (
              <>
                {!selected.stickers.length ? (
                  <Center h={100}><Text c="dimmed" size="sm">{t('pages.stickers.noStickers')}</Text></Center>
                ) : (
                  <>
                    <Text size="xs" c="dimmed" mb="xs">{t('pages.stickers.dragHint')}</Text>
                    <SimpleGrid cols={{ base: 4, sm: 6, md: 8 }} spacing="sm">
                      {selected.stickers.map((s) => (
                        <Box
                          key={s.id}
                          pos="relative"
                          draggable
                          onDragStart={(e) => { setDragId(s.id); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragOver={(e) => { e.preventDefault(); if (overId !== s.id) setOverId(s.id); }}
                          onDragLeave={() => setOverId((v) => (v === s.id ? null : v))}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (dragId) moveSticker(dragId, s.id);
                            setDragId(null); setOverId(null);
                          }}
                          onDragEnd={() => { setDragId(null); setOverId(null); }}
                          onClick={() => setPreview(s)}
                          style={{
                            aspectRatio: '1',
                            cursor: 'grab',
                            opacity: dragId === s.id ? 0.35 : 1,
                            borderRadius: 8,
                            outline: overId === s.id && dragId && dragId !== s.id
                              ? '2px dashed var(--mantine-color-brand-5)'
                              : undefined,
                            transition: 'opacity 120ms ease',
                          }}
                        >
                          <Image
                            src={fileUrl(s.imageKey)}
                            radius="md"
                            fit="contain"
                            h="100%"
                            draggable={false}
                            style={{ background: 'var(--mantine-color-gray-0)', pointerEvents: 'none' }}
                          />
                          <ActionIcon
                            pos="absolute" top={-6} right={-6} size="sm" radius="xl" color="red" variant="filled"
                            onClick={(e) => { e.stopPropagation(); confirmDelete(t('pages.stickers.one'), () => deleteStickerMut.mutate(s.id)); }}
                           loading={deleteStickerMut.isPending}>
                            <IconTrash size={12} />
                          </ActionIcon>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </>
                )}
              </>
            )}
          </SectionCard>
        </Group>
      )}

      {/* Stiker preview — bosilganda katta ko'rinish */}
      <Modal
        opened={!!preview}
        onClose={() => setPreview(null)}
        title={selected?.title ?? t('pages.stickers.one')}
        centered
        size="sm"
      >
        {preview ? (
          <Stack align="center" gap="md">
            <Box
              p="lg"
              style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 16 }}
            >
              <Image src={fileUrl(preview.imageKey)} w={220} h={220} fit="contain" />
            </Box>
            <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>{preview.imageKey}</Text>
            <Group>
              <Button variant="default" onClick={() => setPreview(null)}>{t('common.cancel')}</Button>
              <Button
                color="red"
                leftSection={<IconTrash size={15} />}
                onClick={() => {
                  const id = preview.id;
                  setPreview(null);
                  confirmDelete(t('pages.stickers.one'), () => deleteStickerMut.mutate(id));
                }}
               loading={deleteStickerMut.isPending}>
                {t('common.delete')}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>

      <Modal opened={opened} onClose={close} title={editPack ? t('pages.stickers.editPack') : t('pages.stickers.addPack')}>
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput label={t('pages.stickers.packTitle')} required {...form.getInputProps('title')} />
            <NumberInput label={t('pages.stickers.order')} min={0} {...form.getInputProps('order')} />
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={saveMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
